import { useState } from 'react';
import { searchDocuments, getCases } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  FileText, 
  FileType, 
  Image as ImageIcon,
  Loader2,
  ArrowRight
} from 'lucide-react';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [caseFilter, setCaseFilter] = useState('all');
  const [cases, setCases] = useState([]);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const res = await getCases();
      setCases(res.data);
    } catch (error) {
      console.error('Failed to load cases');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setSearching(true);
    setHasSearched(true);

    try {
      const res = await searchDocuments(query, caseFilter === 'all' ? null : caseFilter);
      setResults(res.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
      case 'docx': return <FileType className="w-5 h-5 text-blue-500" />;
      case 'image': return <ImageIcon className="w-5 h-5 text-green-500" />;
      default: return <FileText className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const highlightMatch = (text, query) => {
    if (!text || !query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} className="bg-bronze/30 text-foreground px-0.5 rounded">{part}</mark>
        : part
    );
  };

  const getCaseName = (caseId) => {
    const c = cases.find(c => c.id === caseId);
    return c?.title || 'Unknown Case';
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="search-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
            Search Documents
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Search across all your case documents
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search document content..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-11 h-12 font-body text-lg"
              data-testid="search-input"
            />
          </div>
          <Select value={caseFilter} onValueChange={setCaseFilter}>
            <SelectTrigger className="w-full sm:w-64 h-12" data-testid="case-filter-select">
              <SelectValue placeholder="Filter by case" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cases</SelectItem>
              {cases.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            type="submit" 
            disabled={searching}
            className="h-12 px-8 bg-navy hover:bg-navy-light font-body"
            data-testid="search-btn"
          >
            {searching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Search
              </>
            )}
          </Button>
        </form>

        {/* Results */}
        {hasSearched && (
          <div className="space-y-4">
            <p className="font-body text-muted-foreground">
              {results.length} result(s) found for "{query}"
            </p>

            {results.length > 0 ? (
              <div className="grid gap-4">
                {results.map((doc, index) => (
                  <Card 
                    key={doc.id} 
                    className="border-border shadow-sm hover:border-bronze/30 transition-all duration-200 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`search-result-${doc.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                          {getFileIcon(doc.file_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-body font-semibold text-foreground">
                              {doc.filename}
                            </h3>
                            <Badge variant="outline" className="font-mono text-xs">
                              {formatFileSize(doc.file_size)}
                            </Badge>
                            {doc.is_exhibit && (
                              <Badge className="bg-success text-white text-xs">
                                Exhibit
                              </Badge>
                            )}
                          </div>
                          <p className="font-body text-sm text-muted-foreground mb-2">
                            Case: {getCaseName(doc.case_id)}
                          </p>
                          {doc.content_preview && (
                            <p className="font-body text-sm text-foreground line-clamp-3">
                              {highlightMatch(doc.content_preview.substring(0, 300), query)}...
                            </p>
                          )}
                        </div>
                        <Link to={`/cases/${doc.case_id}`}>
                          <Button variant="ghost" size="sm" className="font-body text-bronze">
                            View Case
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2">
                <CardContent className="p-12 text-center">
                  <Search className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-heading text-lg text-foreground mb-2">No results found</h3>
                  <p className="font-body text-muted-foreground">
                    Try a different search term or filter
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {!hasSearched && (
          <Card className="border-dashed border-2">
            <CardContent className="p-12 text-center">
              <Search className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl text-foreground mb-2">
                Search your documents
              </h3>
              <p className="font-body text-muted-foreground max-w-md mx-auto">
                Enter keywords to search across all document content. 
                Filter by case to narrow down results.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
